import { ArrowRight } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { toast } from "@/shared/lib/toast";
import { Modal } from "@/shared/ui/modal";
import { Select } from "@/shared/ui/select";

import { useTransferStock } from "../api/use-transfer-stock";

type Marketplace = "shopee" | "lazada" | "tiktok";

const MKPS: Marketplace[] = ["shopee", "lazada", "tiktok"];
const MKP_LABEL: Record<Marketplace, string> = {
  shopee: "Shopee",
  lazada: "Lazada",
  tiktok: "TikTok",
};

function otherMkp(m: Marketplace): Marketplace {
  return m === "tiktok" ? "shopee" : "tiktok";
}

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  sku: string;
  productName?: string;
  fromMkp: Marketplace;
  onHandByMkp: Record<Marketplace, number>;
}

export function TransferModal({
  open,
  onClose,
  sku,
  productName,
  fromMkp,
  onHandByMkp,
}: TransferModalProps): JSX.Element | null {
  const { t } = useTranslation("inventory");
  const [from, setFrom] = useState<Marketplace>(fromMkp);
  const [to, setTo] = useState<Marketplace>(otherMkp(fromMkp));
  const [qty, setQty] = useState(1);
  const [remark, setRemark] = useState("");
  const [beError, setBeError] = useState<string | null>(null);
  const { mutate, isPending } = useTransferStock();

  const fromOnHand = onHandByMkp[from] ?? 0;
  const toOnHand = onHandByMkp[to] ?? 0;
  const sameErr = from === to;
  const oversold = !sameErr && qty > fromOnHand;
  const invalid = sameErr || qty <= 0;

  function handleConfirm(): void {
    setBeError(null);
    const trimmedRemark = remark.trim();
    mutate(
      {
        sku,
        from_marketplace: from,
        to_marketplace: to,
        qty,
        // Only include the remark when non-empty — absent === stored as nothing.
        ...(trimmedRemark ? { remark: trimmedRemark } : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            t("transfer.toast_done" as never, { qty, from: MKP_LABEL[from], to: MKP_LABEL[to] }),
          );
          onClose();
        },
        onError: () => {
          setBeError(t("transfer.err_insufficient" as never));
          toast.error(t("transfer.toast_error" as never));
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<span data-testid="transfer-modal-title">{t("transfer.title" as never)}</span>}
      subtitle={productName ? `${sku} · ${productName}` : sku}
      footer={
        <div className="flex w-full gap-2.5">
          <button
            type="button"
            data-testid="transfer-cancel"
            onClick={onClose}
            className="flex-1 rounded-[10px] border border-[var(--border-default)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)]"
          >
            {t("transfer.cancel" as never)}
          </button>
          <button
            type="button"
            data-testid="transfer-confirm"
            disabled={invalid || isPending}
            onClick={handleConfirm}
            className="flex-1 rounded-[10px] px-4 py-2.5 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              background: "var(--grad-primary-btn)",
              boxShadow: "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5)",
            }}
          >
            {t("transfer.confirm" as never)}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Route */}
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">
            {t("transfer.route" as never)}
          </div>
          <div className="flex items-center gap-2.5">
            <Select
              aria-label={t("transfer.from" as never)}
              value={from}
              onChange={(e) => {
                setFrom(e.target.value as Marketplace);
                setBeError(null);
              }}
              className="flex-1"
            >
              {MKPS.map((m) => (
                <option key={m} value={m}>
                  {MKP_LABEL[m]}
                </option>
              ))}
            </Select>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--canvas)] text-[var(--text-subtle)]">
              <ArrowRight className="h-4 w-4" />
            </span>
            <Select
              aria-label={t("transfer.to" as never)}
              value={to}
              onChange={(e) => {
                setTo(e.target.value as Marketplace);
                setBeError(null);
              }}
              className="flex-1"
            >
              {MKPS.map((m) => (
                <option key={m} value={m}>
                  {MKP_LABEL[m]}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Qty */}
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">
            {t("transfer.qty" as never)}
          </div>
          <input
            aria-label={t("transfer.qty" as never)}
            value={String(qty)}
            onChange={(e) => {
              setQty(parseInt(e.target.value, 10) || 0);
              setBeError(null);
            }}
            className="num h-10 w-28 rounded-[10px] border border-[var(--premium-border-strong)] px-3 text-right text-sm outline-none focus:border-[var(--brand-500)] focus-ring"
          />
        </div>

        {/* Remark (optional audit context, PRD v1.8 §4.2) */}
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">
            {t("remark.label" as never)}
          </div>
          <input
            aria-label={t("remark.label" as never)}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder={t("remark.placeholder" as never)}
            maxLength={500}
            data-testid="transfer-remark"
            className="h-10 w-full rounded-[10px] border border-[var(--premium-border-strong)] px-3 text-sm outline-none focus:border-[var(--brand-500)] focus-ring"
          />
        </div>

        {/* Live preview */}
        <div
          className="rounded-xl border border-[var(--brand-100,#d0e2ff)] p-3.5 text-[13px]"
          style={{ background: "linear-gradient(180deg,#f3f8ff,#eef5ff)" }}
        >
          <div className="flex items-center justify-between py-0.5">
            <span>{t("transfer.preview_onhand" as never, { mkp: MKP_LABEL[from] })}</span>
            <span className="num font-semibold">
              {fromOnHand} →{" "}
              <span className={fromOnHand - qty < 0 ? "text-[var(--intent-danger-fg)]" : ""}>
                {fromOnHand - qty}
              </span>
            </span>
          </div>
          <div className="flex items-center justify-between py-0.5">
            <span>{t("transfer.preview_onhand" as never, { mkp: MKP_LABEL[to] })}</span>
            <span className="num font-semibold">
              {toOnHand} → <span className="text-[var(--intent-success-fg)]">{toOnHand + qty}</span>
            </span>
          </div>
          {sameErr && (
            <div className="mt-1.5 font-semibold text-[var(--intent-danger-fg)]">
              {t("transfer.err_same" as never)}
            </div>
          )}
          {oversold && (
            <div
              data-testid="transfer-warn-oversold"
              className="mt-1.5 font-semibold text-[var(--intent-warning-fg)]"
            >
              {t("transfer.warn_oversold" as never)}
            </div>
          )}
          {beError && (
            <div
              data-testid="transfer-error-be"
              className="mt-1.5 font-semibold text-[var(--intent-danger-fg)]"
            >
              {beError}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
